/**
 * Selection commands (W2-11 batch-tools; DESIGN.md §5.4, §5.8; WORKPACKAGES Appendix B `select.*`; U§5 B5).
 *
 *  - `select.similar` (Shift+S): notes of the same type, side and real/fake on the same line(s); segments with the
 *    same property, layer and curve (keys: same property and layer) on the same line(s). Run again with nothing new
 *    to add, it widens to every line.
 *  - `select.rangeStart` / `select.rangeEnd` (RPE "Range start / end", X15-14): mark a beat, then select everything
 *    of the active line between the mark and the second beat (notes, and keys of its motion). From the lane's
 *    context menu the beats are the right-clicked ones (contextBeat.ts); from keys, menus and the palette the
 *    playhead.
 *  - `select.everyNth`: keep every Nth item of the selection (in time order, per kind).
 *  - `select.invert`: invert the note selection on the active line (or the key selection on the selected tracks).
 */

import type { CommandContext, CommandDef } from '../../app/registry/types';
import type { Ease } from '../../core/ease/types';
import type { KeyId, LineId, Note, NoteId, TrackId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat } from '../../core/time/types';
import type { DocStore } from '../../state/types';
import { noteTimeOrder } from '../../core/tools/common';
import { m } from './batch.messages';
import { ref, selectionIds, workingLine, type Enabled } from './batchContext';
import { actionBeat } from './contextBeat';

/** True, or why `select.none` has nothing to do. */
function anySelected(ctx: CommandContext): Enabled {
  const sel = ctx.editor.getState().selection;
  const any =
    sel.notes.size + sel.keys.size + sel.segments.size + sel.lines.size + sel.bpm.size + sel.markers.size > 0 ||
    sel.effects.size + sel.videos.size + sel.groups.size > 0;
  return any ? true : ref(m.reasonNothingToClear);
}

function easeKey(e: Ease): string {
  switch (e.kind) {
    case 'hold':
      return 'hold';
    case 'tween':
      return `t${String(e.id)}:${String(e.left)}:${String(e.right)}`;
    case 'bezier':
      return `b${e.p.join(',')}`;
  }
}

function noteSig(n: Note): string {
  return `${n.kind}|${String(n.above)}|${String(n.fake)}`;
}

function trackSig(store: DocStore, track: TrackId): { line: LineId; sig: string } | null {
  const owner = store.doc.tracks.get(track)?.owner;
  if (owner?.kind !== 'line') return null;
  return { line: owner.line, sig: `${owner.prop}|${String(owner.layer)}` };
}

export interface SimilarSets {
  readonly notes: Set<NoteId>;
  readonly keys: Set<KeyId>;
  readonly segments: Set<KeyId>;
}

/** Items similar to the selection, on the selection's lines (`allLines` false) or on every line. */
export function similarTo(
  store: DocStore,
  sel: { readonly notes: readonly NoteId[]; readonly keys: readonly KeyId[]; readonly segments: readonly KeyId[] },
  allLines: boolean,
): SimilarSets {
  const notes = new Set<NoteId>();
  const keys = new Set<KeyId>();
  const segments = new Set<KeyId>();
  const noteSigs = new Set<string>();
  const noteLines = new Set<LineId>();
  for (const id of sel.notes) {
    const n = store.doc.notes.get(id);
    if (n === undefined) continue;
    noteSigs.add(noteSig(n));
    noteLines.add(n.line);
  }
  if (noteSigs.size > 0) {
    for (const n of store.doc.notes.values()) {
      if ((allLines || noteLines.has(n.line)) && noteSigs.has(noteSig(n))) notes.add(n.id);
    }
  }
  const keySigs = new Set<string>();
  const segSigs = new Set<string>();
  const lines = new Set<LineId>();
  for (const id of sel.keys) {
    const k = store.doc.keys.get(id);
    const t = k === undefined ? null : trackSig(store, k.track);
    if (t === null) continue;
    keySigs.add(t.sig);
    lines.add(t.line);
  }
  for (const id of sel.segments) {
    const k = store.doc.keys.get(id);
    const t = k === undefined ? null : trackSig(store, k.track);
    if (k === undefined || t === null) continue;
    segSigs.add(`${t.sig}|${easeKey(k.ease)}`);
    lines.add(t.line);
  }
  if (keySigs.size > 0 || segSigs.size > 0) {
    for (const track of store.doc.tracks.values()) {
      const t = trackSig(store, track.id);
      if (t === null || !(allLines || lines.has(t.line))) continue;
      const ids = store.idx.keysByTrack(track.id);
      ids.forEach((id, i) => {
        const k = store.doc.keys.get(id);
        if (k === undefined) return;
        if (keySigs.has(t.sig)) keys.add(id);
        if (i + 1 < ids.length && segSigs.has(`${t.sig}|${easeKey(k.ease)}`)) segments.add(id);
      });
    }
  }
  return { notes, keys, segments };
}

function hasAll<T>(set: ReadonlySet<T>, items: Iterable<T>): boolean {
  for (const x of items) if (!set.has(x)) return false;
  return true;
}

function needSelection(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  const ids = selectionIds(ctx);
  return ids.notes.length + ids.keys.length + ids.segments.length > 0 ? true : ref(m.reasonNothingSelected);
}

/** Range marks per document store. */
const rangeStarts = new WeakMap<DocStore, Beat>();

/** Notes of a line and keys of its tracks inside [a, b]. */
export function itemsInRange(store: DocStore, line: LineId, a: Beat, b: Beat): { notes: NoteId[]; keys: KeyId[] } {
  const lo = Beats.min(a, b);
  const hi = Beats.max(a, b);
  const notes = store.idx.notesByLine(line).filter((id) => {
    const n = store.doc.notes.get(id);
    return n !== undefined && Beats.lte(lo, n.t) && Beats.lte(n.t, hi);
  });
  const keys: KeyId[] = [];
  for (const ids of store.idx.tracksOfLine(line).values()) {
    for (const t of ids) {
      for (const k of store.idx.keysByTrack(t)) {
        const key = store.doc.keys.get(k);
        if (key !== undefined && Beats.lte(lo, key.t) && Beats.lte(key.t, hi)) keys.push(k);
      }
    }
  }
  return { notes, keys };
}

/** Keeps every Nth item (0, N, 2N, ...) of each kind, in time order. */
export function everyNth(
  store: DocStore,
  sel: { readonly notes: readonly NoteId[]; readonly keys: readonly KeyId[]; readonly segments: readonly KeyId[] },
  n: number,
): SimilarSets {
  const keepNotes = sel.notes
    .map((id) => store.doc.notes.get(id))
    .filter((x): x is Note => x !== undefined)
    .sort(noteTimeOrder)
    .filter((_, i) => i % n === 0)
    .map((x) => x.id);
  const byTime = (ids: readonly KeyId[]): KeyId[] =>
    [...ids]
      .filter((id) => store.doc.keys.has(id))
      .sort((a, b) => {
        const ka = store.doc.keys.get(a);
        const kb = store.doc.keys.get(b);
        return ka === undefined || kb === undefined ? 0 : Beats.cmp(ka.t, kb.t) || a - b;
      })
      .filter((_, i) => i % n === 0);
  return { notes: new Set(keepNotes), keys: new Set(byTime(sel.keys)), segments: new Set(byTime(sel.segments)) };
}

function nthArg(args: unknown): number | null {
  const v =
    typeof args === 'number'
      ? args
      : typeof args === 'object' && args !== null
        ? (args as { n?: unknown }).n
        : undefined;
  return typeof v === 'number' && Number.isSafeInteger(v) && v >= 2 ? v : null;
}

export const SELECT_COMMANDS: readonly CommandDef[] = [
  {
    id: 'select.similar',
    title: m.cmdSelectSimilar,
    category: 'Select',
    icon: 'ListFilter',
    aliases: ['similar', 'same type', 'same easing', '选择相似'],
    level: 'guided',
    hint: 40,
    enabled: needSelection,
    run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const ids = selectionIds(ctx);
      const sel = ctx.editor.getState().selection;
      let sets = similarTo(store, ids, false);
      let widened = false;
      const nothingNew =
        hasAll(sel.notes, sets.notes) && hasAll(sel.keys, sets.keys) && hasAll(sel.segments, sets.segments);
      if (nothingNew) {
        sets = similarTo(store, ids, true);
        widened = true;
      }
      ctx.editor.getState().select({ notes: sets.notes, keys: sets.keys, segments: sets.segments }, 'replace');
      ctx.ui.toast(
        ref(widened ? m.similarAllLines : m.similarLine, {
          count: sets.notes.size + sets.keys.size + sets.segments.size,
        }),
        { kind: 'info', durationMs: 2500 },
      );
    },
    menus: [
      { menu: 'lane.context', group: 'select', order: 10 },
      { menu: 'motion.context', group: 'select', order: 10 },
      { menu: 'app.edit', group: 'select', order: 10 },
    ],
  },
  {
    id: 'select.rangeStart',
    title: m.cmdRangeStart,
    category: 'Select',
    icon: 'ArrowDownToLine',
    aliases: ['range start', 'X15-14'],
    level: 'full',
    enabled: (ctx) => (ctx.store === null ? ref(m.reasonNoProject) : true),
    run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const at = actionBeat(ctx);
      rangeStarts.set(store, at);
      ctx.ui.toast(ref(m.rangeStartSet, { beat: formatBeat(at) }), { kind: 'info', durationMs: 3000 });
    },
    menus: [
      { menu: 'lane.context', group: 'select', order: 20 },
      { menu: 'motion.context', group: 'select', order: 20 },
    ],
  },
  {
    id: 'select.rangeEnd',
    title: m.cmdRangeEnd,
    category: 'Select',
    icon: 'ArrowUpToLine',
    aliases: ['range end', 'X15-14'],
    level: 'full',
    enabled(ctx) {
      const store = ctx.store;
      if (store === null) return ref(m.reasonNoProject);
      if (!rangeStarts.has(store)) return ref(m.reasonNoRangeStart);
      return workingLine(ctx) === null ? ref(m.reasonNoLine) : true;
    },
    run(ctx) {
      const store = ctx.store;
      const line = workingLine(ctx);
      const start = store === null ? undefined : rangeStarts.get(store);
      if (store === null || line === null || start === undefined) return;
      const r = itemsInRange(store, line, start, actionBeat(ctx));
      ctx.editor.getState().select({ notes: new Set(r.notes), keys: new Set(r.keys), segments: new Set() }, 'replace');
      ctx.ui.toast(ref(m.rangeSelected, { notes: r.notes.length, keys: r.keys.length }), {
        kind: 'info',
        durationMs: 2500,
      });
    },
    menus: [
      { menu: 'lane.context', group: 'select', order: 21 },
      { menu: 'motion.context', group: 'select', order: 21 },
    ],
  },
  {
    id: 'select.everyNth',
    title: m.cmdEveryNth,
    category: 'Select',
    icon: 'ListOrdered',
    aliases: ['every other', 'every nth', 'thin out'],
    level: 'full',
    args: { kind: 'number', label: ref(m.everyNthArg), min: 2, max: 64 },
    enabled: needSelection,
    async run(ctx, args) {
      const store = ctx.store;
      if (store === null) return;
      let n = nthArg(args);
      if (n === null) {
        const picked = await ctx.ui.choose<string>({
          title: ref(m.cmdEveryNth),
          options: [2, 3, 4, 8].map((k) => ({ value: String(k), label: ref(m.everyNthOption, { n: k }) })),
        });
        if (picked === null) return;
        n = Number(picked);
      }
      const sets = everyNth(store, selectionIds(ctx), n);
      ctx.editor.getState().select({ notes: sets.notes, keys: sets.keys, segments: sets.segments }, 'replace');
    },
    menus: [{ menu: 'app.edit', group: 'select', order: 20 }],
  },
  {
    id: 'select.invert',
    title: m.cmdInvert,
    category: 'Select',
    icon: 'FlipVertical2',
    aliases: ['invert selection'],
    level: 'full',
    enabled: (ctx) =>
      ctx.store === null ? ref(m.reasonNoProject) : workingLine(ctx) === null ? ref(m.reasonNoLine) : true,
    run(ctx) {
      const store = ctx.store;
      const line = workingLine(ctx);
      if (store === null || line === null) return;
      const ids = selectionIds(ctx);
      if (ids.keys.length > 0 || ids.segments.length > 0) {
        const tracks = new Set<TrackId>();
        for (const id of [...ids.keys, ...ids.segments]) {
          const k = store.doc.keys.get(id);
          if (k !== undefined) tracks.add(k.track);
        }
        const sel = new Set(ids.keys);
        const next = new Set<KeyId>();
        for (const t of tracks) for (const k of store.idx.keysByTrack(t)) if (!sel.has(k)) next.add(k);
        ctx.editor.getState().select({ keys: next, segments: new Set(), notes: new Set() }, 'replace');
        return;
      }
      const sel = new Set(ids.notes);
      const next = new Set(store.idx.notesByLine(line).filter((id) => !sel.has(id)));
      ctx.editor.getState().select({ notes: next, keys: new Set(), segments: new Set() }, 'replace');
    },
    menus: [{ menu: 'app.edit', group: 'select', order: 30 }],
  },
  {
    // Esc clears the selection (DESIGN.md §8.2 "Selection ... clear: Esc"; RPE "Esc clears the multi-selection",
    // E§3.20). Its Escape binding (batch.module.tsx) gives way to placement, the paste ghost, drags and the tool sheet.
    id: 'select.none',
    title: m.cmdSelectNone,
    category: 'Select',
    icon: 'SquareDashed',
    aliases: ['deselect', 'clear selection', 'select none'],
    level: 'guided',
    enabled: anySelected,
    run(ctx) {
      ctx.editor.getState().clearSelection();
    },
  },
];
